'use client';

import { useContext, createContext } from 'react';
import { useI18n } from '~/features/i18n';
import type { NotificationDispatcher } from './NotificationDispatcher';

// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const NotificationsContext = createContext<NotificationDispatcher>(null!);

export default function useNotifications() {
    const notificationDispatcher = useContext(NotificationsContext);
    if (!notificationDispatcher) {
        throw new Error('This hook needs to be called inside a NotificationsContext provider');
    }

    const i18n = useI18n();

    type DispatchData = Partial<Omit<NotificationDispatcher.NotificationData, 'type'>>;

    return {
        dispatchError(data: DispatchData = {}) {
            notificationDispatcher.dispatch({
                type: 'ERROR',
                title: i18n._('Error'),
                message: i18n._('Oops, looks like something went wrong, please try again later.'),
                ...data,
            });
        },
        dispatchWarning(data: DispatchData = {}) {
            notificationDispatcher.dispatch({
                type: 'WARNING',
                title: i18n._('Warning'),
                message: i18n._('Careful, a warning was triggered.'),
                ...data,
            });
        },
        dispatchSuccess(data: DispatchData = {}) {
            notificationDispatcher.dispatch({
                type: 'SUCCESS',
                title: i18n._('Success'),
                message: i18n._('Your request was handled successfully.'),
                ...data,
            });
        },
        dispatchGraphqlError(error: any = {}, data: DispatchData = {}) {
            let message = i18n._('Oops, looks like something went wrong, please try again later.');
            // TODO: re-evaluate current error handling, since we don't have nested errors in 'extensions' anymore
            // OLD: `parseNestedErrors(fields)`
            if (error.response?.errors?.length > 0) {
                const firstError = error.response.errors[0];
                message = firstError.message;
            } else if (error.message) {
                message = error.message;
            }

            return notificationDispatcher.dispatch({
                type: 'ERROR',
                title: i18n._('Error'),
                message,
                ...data,
            });
        },
        dispatch(data: NotificationDispatcher.NotificationData) {
            notificationDispatcher.dispatch(data);
        },
        removeNotification(notification: NotificationDispatcher.Notification) {
            notificationDispatcher.removeNotification(notification);
        },
    } as const;
}
